import { LabeledValue, useFormatting } from "@msys/ui";
import { Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { getPersonLabel } from "../users/helpers.js";
import { UserAvatar } from "../users/UserAvatar.js";
import { UserAvatarFragment } from "../users/UserAvatar.generated.js";
import { LastModifiedInfoBoxFragment } from "./LastModifiedInfo.generated.js";

interface Props {
  lastModified: LastModifiedInfoBoxFragment | null | undefined;
  createdBy: UserAvatarFragment;
  createdAt: string | Date | null | undefined;
  // false on surfaces that already display creation info (the template modal's
  // own "Created by"), so the fallback is not rendered as a duplicate.
  showCreationFallback?: boolean;
}

// Read-only "last modified" entry (RF-263). Renders the captured user actor, or
// "System" for a system actor, or — when nothing was captured and the surface
// has no creation field of its own — a creation fallback from the entity's
// author + creation timestamp.
export const LastModifiedInfo = ({
  lastModified,
  createdBy,
  createdAt,
  showCreationFallback = true,
}: Props) => {
  const { t } = useTranslate("Global");

  if (!lastModified) {
    if (!showCreationFallback) {
      return null;
    }
    return (
      <LabeledValue label={t("Created by", { ns: "Global" })}>
        <UserActorLine user={createdBy} at={createdAt} />
      </LabeledValue>
    );
  }

  return (
    <LabeledValue label={t("Last modified", { ns: "Global" })}>
      {lastModified.by ? (
        <UserActorLine user={lastModified.by} at={lastModified.at} />
      ) : (
        <SystemActorLine at={lastModified.at} />
      )}
    </LabeledValue>
  );
};

const UserActorLine = ({
  user,
  at,
}: {
  user: UserAvatarFragment;
  at: string | Date | null | undefined;
}) => {
  const { t } = useTranslate("Global");
  const { getFormattedDateTime } = useFormatting();
  const name = getPersonLabel(user, false);

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <UserAvatar userAvatar={user} size="s" />
      <Typography variant="body1">
        {name.length > 0 ? name : t("Unknown user", { ns: "Global" })}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {at ? getFormattedDateTime(at) : "-"}
      </Typography>
    </Stack>
  );
};

const SystemActorLine = ({ at }: { at: string | Date | null | undefined }) => {
  const { t } = useTranslate("Global");
  const { getFormattedDateTime } = useFormatting();

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
      <Typography variant="body1">{t("System", { ns: "Global" })}</Typography>
      <Typography variant="body2" color="text.secondary">
        {at ? getFormattedDateTime(at) : "-"}
      </Typography>
    </Stack>
  );
};
